import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { ButtonGroup, MenuItem, MenuList } from "@mui/material";
import { ActionAutoService, ApiService, CategoryService, SearchCardService } from "../../api";
import {
	AiAgentConfigDialog,
	AlertBanner,
	AutomationDialog,
	CategoryDeleteDialog,
	CategoryDialog,
	CircularLoader,
	createNotification,
	CustomButton,
	CustomDialog,
	CustomTooltip,
	DropDown,
	EmptyState,
	ExpandableCategoryRow,
	HeaderCategoryRow,
	icon,
	IconComponent,
	SearchInput,
	withWebsocket,
} from "../../common/components";
import {
	environmentSwitch,
	Flags,
	hasCompanyPermission,
	hasPermission,
	isSegFeatureEnabled,
	Permissions,
	SegFlags,
	translate,
} from "../../common/providers";
import { removeFavoriteCategory } from "../../common/slice";
import {
	convertAnyDateToDisplayDate,
	debounce,
	isNonEmptyArray,
	searchInTree,
	updateTreeBranch,
} from "../../common/utils";
import { exportView } from "../../navigation";
import styles from "./AdminCategories.module.css";
import { CategorySubscriptionsDialog, ImportTransferCategorySidePanel } from "./components";
import { ACTION_TYPES } from "./constants/constants";
import { addCategories, deleteCategory, getParent, getCategoryFromTree } from "./utils/utils";
import { useApi, useTrackEvent } from "../../common/hooks";
import { ACTION_OPTION, DOCUMENT_OPTION } from "../../common/constants";

const debouncedSearch = debounce((searchFunction) => {
	searchFunction();
}, 500);

const AdminCategories = ({ subscribeWS }) => {
	const projectId = useSelector(({ context }) => context.project.id);
	const hasAvailableAIQuota = useSelector(({ context }) => context?.hasAvailableAIQuota);
	const [categories, setCategories] = useState([]);
	const [isTreeLoading, setIsTreeLoading] = useState(false);
	const [isNew, setIsNew] = useState(true);
	const [openCategoryDialog, setOpenCategoryDialog] = useState(false);
	const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
	const [searchValue, setSearchValue] = useState("");
	const [debouncedSearchValue, setDebouncedSearchValue] = useState("");
	const [tempCategory, setTempCategory] = useState({});
	const [tempParent, setTempParent] = useState({});
	const [openImportSidePanel, setOpenImportSidePanel] = useState(false);
	const [openTransferSidePanel, setOpenTransferSidePanel] = useState(false);
	const [needUpdate, setNeedUpdate] = useState(false);
	const [openAutomationDialog, setOpenAutomationDialog] = useState(false);
	const [selectedCategory, setSelectedCategory] = useState(null);
	const [selectedAitCategorization, setSelectedAitCategorization] = useState(null);
	const [openDropDown, setOpenDropDown] = useState(false);
	const [tempAutomation, setTempAutomation] = useState(null);
	const [openAiCategorization, setOpenAiCategorization] = useState(false);
	const [openAiCatCreation, setOpenAiCatCreation] = useState(false);
	const [isGenerating, setIsGenerating] = useState(false);
	const [activeCategorizations, setActiveCategorizations] = useState([]);
	const [openWarningPopup, setOpenWarningPopUp] = useState(false);
	const categoriesRef = useRef(categories);
	const cancelTokenSourceRef = useRef(null);
	const updateCancelTokenSourceRef = useRef(null);
	const importMenuButtonRef = useRef(null);
	const trackEvent = useTrackEvent();
	const anchorRef = useRef(null);
	const { call: trigger } = useApi(ActionAutoService.trigger);
	const { call: getAutomations, cancel: cancelGetAutomations } = useApi(CategoryService.getThematicAutomations);
	const { call: getFilters } = useApi(SearchCardService.getFilters);
	const { call: updateFilters } = useApi(SearchCardService.updateFilters);
	const { call: saveCategorizationAgentConfig } = useApi(CategoryService.saveCategorizationAgentConfig);
	const { call: getParametersForInformation } = useApi(CategoryService.getCategorizationAgentConfig);
	const { call: rollbackCategorizationAgentResults } = useApi(CategoryService.rollbackCategorizationAgentResults);
	const { call: launchCategorizationAgent } = useApi(CategoryService.launchCategorizationAgent);
	const { call: getCategorizationLaunchHistory } = useApi(CategoryService.getCategorizationLaunchHistory);
	const { call: launchCategoryTreeCreation } = useApi(CategoryService.launchCategoryTreeCreation);
	const { call: getTreeCreationAgentConfig } = useApi(CategoryService.getTreeCreationAgentConfig);
	const { call: saveTreeCreationAgentConfig } = useApi(CategoryService.saveTreeCreationAgentConfig);
	const { call: getTreeCreationAgentHistory } = useApi(CategoryService.getTreeCreationAgentHistory);
	const { call: launchGlobalCategorizationAgent } = useApi(CategoryService.launchGlobalCategorizationAgent);
	const { call: getThematicInformation } = useApi(CategoryService.getThematicInformation);
	const { call: getActiveCategorizations } = useApi(CategoryService.getActiveCategorizations);

	const dispatch = useDispatch();
	const isAllowedToEdit = useMemo(
		() =>
			environmentSwitch({
				main:
					hasCompanyPermission([Permissions.PROJECT_LEADER, Permissions.PROJECT_MANAGER]) &&
					hasPermission([Permissions.PROJECT_LEADER]),
				veolia: true,
				smac: true,
			}),
		[]
	);
	const isAllowedToEditStrict = useMemo(
		() =>
			environmentSwitch({
				main: hasCompanyPermission([Permissions.PROJECT_LEADER]) && hasPermission([Permissions.PROJECT_LEADER]),
				veolia: true,
				smac: true,
			}),
		[]
	);
	const categorizationDocOptions = useMemo(
		() => [
			{
				value: DOCUMENT_OPTION.AI,
				label: translate("common:component.ai.document-choice-for-analysis.ai-option"),
			},
			{
				label: translate("common:component.ai.document-choice-for-analysis.manual-option"),
				value: DOCUMENT_OPTION.MANUAL,
			},
		],
		[]
	);
	const aiCreationDocOptions = useMemo(
		() => [
			{
				label: translate("common:component.ai.document-choice-for-analysis.manual-option"),
				value: DOCUMENT_OPTION.MANUAL,
			},
			{
				value: DOCUMENT_OPTION.AI,
				label: translate("common:component.ai.document-choice-for-analysis.ai-option"),
			},
			{
				label: translate("common:component.ai.document-choice-for-analysis.no-documents"),
				value: DOCUMENT_OPTION.NONE,
			},
		],
		[]
	);
	const categorizationActionOptions = useMemo(() => {
		if (selectedAitCategorization) {
			return [
				{
					label: translate("common:component.ai-config.detected-content-integration.answer.add"),
					value: ACTION_OPTION.APPEND,
				},
				{
					value: ACTION_OPTION.REPLACE,
					label: translate("common:component.ai-config.detected-content-integration.answer.replace"),
				},
			];
		}
		return [
			{
				label: translate("common:component.ai-config.detected-content-integration.answer.add"),
				value: ACTION_OPTION.APPEND,
			},
			{
				value: ACTION_OPTION.REPLACE,
				label: translate("common:component.ai-config.detected-content-integration.answer.replace"),
			},
			{
				label: translate("common:component.ai-config.detected-content-integration.answer.default"),
				value: ACTION_OPTION.CATEGORY_POLICY,
			},
		];
	}, [selectedAitCategorization]);
	const aiCreationPromptModels = useMemo(
		() => [
			{
				title: translate("admin-categories.ai-config-dialog.prompt-models.based-on-documents.title"),
				subTitle: translate("admin-categories.ai-config-dialog.prompt-models.based-on-documents.subTitle"),
				value: translate("admin-categories.ai-config-dialog.prompt-models.based-on-documents.value"),
			},
			{
				subTitle: translate("admin-categories.ai-config-dialog.prompt-models.based-on-topics.subTitle"),
				title: translate("admin-categories.ai-config-dialog.prompt-models.based-on-topics.title"),
				value: translate("admin-categories.ai-config-dialog.prompt-models.based-on-topics.value"),
			},
			{
				title: translate("admin-categories.ai-config-dialog.prompt-models.empty.title"),
				subTitle: translate("admin-categories.ai-config-dialog.prompt-models.empty.subTitle"),
				value: "",
			},
		],
		[]
	);
	const aiCategorizationPromptModels = useMemo(
		() => [
			{
				title: translate("common:ai-config-dialog.categorization.prompt-models.schedule-deadline.title"),
				subTitle: translate("common:ai-config-dialog.categorization.prompt-models.schedule-deadline.subTitle"),
				value: translate("common:ai-config-dialog.categorization.prompt-models.schedule-deadline.value"),
			},
			{
				title: translate("common:ai-config-dialog.categorization.prompt-models.penalties.title"),
				subTitle: translate("common:ai-config-dialog.categorization.prompt-models.penalties.subTitle"),
				value: translate("common:ai-config-dialog.categorization.prompt-models.penalties.value"),
			},
			{
				title: translate("common:ai-config-dialog.categorization.prompt-models.empty.title"),
				subTitle: translate("common:ai-config-dialog.categorization.prompt-models.empty.subTitle"),
				value: "",
			},
		],
		[]
	);
	const getTree = useCallback(
		(searchText = "") => {
			setIsTreeLoading(true);
			const request = searchText
				? CategoryService.getTreeForProject(
						{ projectId, search: searchText },
						cancelTokenSourceRef.current.token
				  )
				: CategoryService.getTreeParentsForProject({ projectId }, cancelTokenSourceRef.current.token);
			request
				.then((data) => setCategories(data || []))
				.catch((err) => console.error(err))
				.finally(() => {
					setIsTreeLoading(false);
					setIsGenerating(false);
				});
		},
		[projectId]
	);
	const handleAutomationMessage = ({ reason, featureName, thematicId }) => {
		if (featureName === "CATEGORY_TREE_CREATOR") {
			if (reason === "COMPLETED_SUCCESS") {
				getTree();
			}
			if (reason === "COMPLETED_FAILURE") {
				setIsGenerating(false);
			}
		}
		if (featureName === "CONTENT_CATEGORIZATION") {
			const thematicIdInt = parseInt(thematicId, 10);
			setActiveCategorizations((prev) => prev.filter((ac) => ac !== thematicIdInt));
			const thematicFound = searchInTree({
				tree: categoriesRef.current,
				idToFind: thematicIdInt,
				iterativeBranchName: "subThematic",
			});
			if (reason === "COMPLETED_SUCCESS" && thematicFound?.agentStatus !== "FINISHED") {
				getThematicInformation({ thematicId: thematicIdInt })
					.then((data) => {
						const newCategories = updateTreeBranch({
							tree: categoriesRef.current,
							idToReplace: thematicIdInt,
							newBranch: {
								...thematicFound,
								agentStatus: "FINISHED",
								agentLastModified: convertAnyDateToDisplayDate(new Date()),
								documentsCount: data.documentsCount,
								documentIds: data.documentIds,
								contentsCount: data.contentsCount,
							},
							iterativeBranchName: "subThematic",
						});
						setCategories(newCategories);
					})
					.catch(console.error);
			}
			if (reason === "COMPLETED_FAILURE" && thematicFound.agentStatus !== "FAILED") {
				const newCategories = updateTreeBranch({
					tree: categoriesRef.current,
					idToReplace: thematicIdInt,
					newBranch: {
						...thematicFound,
						agentStatus: "FAILED",
					},
					iterativeBranchName: "subThematic",
				});
				setCategories(newCategories);
			}
		}
	};
	useEffect(() => {
		categoriesRef.current = categories;
	}, [categories]);
	useEffect(() => {
		cancelTokenSourceRef.current = ApiService.getCancelTokenSource();
		const documentTitle = translate("admin-categories.title");
		document.title = documentTitle;
		trackEvent("Category_visit");
		updateCancelTokenSourceRef.current = ApiService.getCancelTokenSource();
		return () => {
			ApiService.cancelTokens(cancelTokenSourceRef.current);
			ApiService.cancelTokens(updateCancelTokenSourceRef.current);
		};
		// eslint-disable-next-line
	}, []);
	useEffect(() => {
		getActiveCategorizations({ projectId })
			.then((data) => setActiveCategorizations(data || []))
			.catch(console.error);
	}, [getActiveCategorizations, projectId]);
	useEffect(() => {
		if (!debouncedSearchValue && !searchValue) {
			getTree(searchValue);
		} else if (searchValue === debouncedSearchValue) {
			ApiService.cancelTokens(updateCancelTokenSourceRef.current);
			updateCancelTokenSourceRef.current = ApiService.getCancelTokenSource();
			setIsTreeLoading(true);
			getTree(searchValue);
		}
		if (needUpdate) {
			setNeedUpdate(false);
		}
	}, [getTree, searchValue, debouncedSearchValue, needUpdate]);
	useEffect(() => {
		subscribeWS(`/topic/ai-agent/projects/${projectId}/refresh`, handleAutomationMessage);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [subscribeWS, projectId]);
	const handleOpenWarningPopUp = () => {
		setOpenWarningPopUp(true);
	};
	const handleCloseWarningPopUp = () => {
		setOpenWarningPopUp(false);
	};
	const handleClickAdd = (parentId) => {
		setOpenCategoryDialog(true);
		setIsNew(true);
		setTempParent(getCategoryFromTree(categories, parentId));
		setTempCategory({});
	};
	const handleClickDelete = (category) => {
		setOpenDeleteDialog(true);
		setTempCategory({ ...category });
		setTempParent(getParent(categories, category.id));
	};
	const handleClickEdit = (category) => {
		setOpenCategoryDialog(true);
		setIsNew(false);
		setTempCategory({ ...category });
		setTempParent(getParent(categories, category.id));
	};
	const handleCloseCategoryDialog = () => {
		setOpenCategoryDialog(false);
	};
	const handleSubmitResponse = (data, oldList) => {
		setCategories((prev) =>
			updateTreeBranch(
				{
					tree: prev,
					idToReplace: data.parentId,
					newBranch: {
						...getCategoryFromTree(categories, data.parentId),
						subThematic: oldList,
						expanded: true,
					},
					iterativeBranchName: "subThematic",
				},
				{ sortKey: "name" }
			)
		);
	};
	const handleSubmitCategoryDialog = (
		newCategory,
		parentChanged,
		newParent,
		callback,
		tempSearchCards,
		automationIdsToDelete
	) => {
		const newParentId = newParent?.id || null;
		let adjustedSearchCards = [...tempSearchCards];
		if (isNonEmptyArray(tempSearchCards)) {
			adjustedSearchCards = tempSearchCards.map((tsc) => {
				if (isNonEmptyArray(tsc?.filters?.keywordFilters?.filters)) {
					tsc.filters.keywordFilters.filters = tsc.filters.keywordFilters.filters.filter((f) =>
						isNonEmptyArray(f.keywords)
					);
					return tsc;
				}
				return undefined;
			});
		}
		const payload = {
			thematicName: newCategory.name,
			description: newCategory.description || null,
			parentId: isNew ? newParentId : newCategory.parentId,
			searches:
				(isNonEmptyArray(adjustedSearchCards) &&
					adjustedSearchCards.map((tsc) => {
						const { name, filters } = tsc;
						return {
							name,
							search: filters,
						};
					})) ||
				null,
		};
		const request =
			(isNew &&
				CategoryService.createFastTrackAutomation(
					{ projectId },
					payload,
					cancelTokenSourceRef.current.token
				)) ||
			CategoryService.updateFastTrackAutomation(
				{ thematicId: newCategory.id },
				{
					...payload,
					automationIdsToDelete: (isNonEmptyArray(automationIdsToDelete) && automationIdsToDelete) || null,
				},
				cancelTokenSourceRef.current.token
			);

		request
			.then((data) => {
				if (data) {
					let newCategories = [];
					let isSet = false;
					if (isNew) {
						isSet = true;
						trackEvent("Category_new");
						if (data.parentId) {
							CategoryService.getTreeChildrenForProject(
								{ parentId: data.parentId },
								cancelTokenSourceRef.current.token
							)
								.then((oldList) => {
									handleSubmitResponse(data, oldList);
								})
								.catch((err) => console.error(err));
						} else {
							getTree(debouncedSearchValue);
						}
					} else {
						const expanded =
							!!newCategory.expanded &&
							Array.isArray(newCategory.subThematic) &&
							newCategory.subThematic.length > 0;
						trackEvent("Category_update");
						if (parentChanged) {
							newCategories = deleteCategory(categories, newCategory.id);
							if (!expanded) {
								CategoryService.getTreeChildrenForProject(
									{ parentId: data.parentId },
									cancelTokenSourceRef.current.token
								)
									.then((oldList) => {
										handleSubmitResponse(data, oldList);
									})
									.catch((err) => console.error(err));
							} else {
								newCategories = addCategories(newCategories, newCategory.parentId, [
									{ ...data, expanded },
								]);
							}
						} else {
							newCategories = updateTreeBranch(
								{
									tree: categories,
									idToReplace: newCategory.id,
									newBranch: {
										...data,
										expanded,
									},
									iterativeBranchName: "subThematic",
								},
								{ sortKey: "name" }
							);
						}
					}
					if (!isSet) {
						setCategories(newCategories);
						if (typeof callback === "function") {
							callback();
						}
					}
				}
				setOpenCategoryDialog(false);
			})
			.catch((err) => {
				console.error(err);
			});
	};
	const handleCloseDeleteDialog = () => {
		setOpenDeleteDialog(false);
	};
	const handleSubmitDeleteDialog = (subThematicKeep) => {
		CategoryService.patchProjectTreeUpdate(
			{ projectId },
			{
				operation: ACTION_TYPES.delete,
				id: tempCategory.id,
				subThematicKeep,
			},
			cancelTokenSourceRef.current.token
		)
			.then(() => {
				let newCategories = [...categories];
				if (subThematicKeep) {
					const { parentId } = tempCategory;
					newCategories = addCategories(
						categories,
						parentId,
						Array.isArray(tempCategory.subThematic) ? tempCategory.subThematic : []
					);
				}
				newCategories = deleteCategory(newCategories, tempCategory.id);
				setOpenDeleteDialog(false);
				setCategories(newCategories);
				dispatch(removeFavoriteCategory(tempCategory.id));
			})
			.catch((err) => {
				console.error(err);
			});
	};
	const handleToggleExpand = (category) => {
		let isExpanded = true;
		if (category.filtered === false && category.expanded === undefined) {
			isExpanded = false;
		}
		if (category.filtered === undefined && !category.expanded) {
			CategoryService.getTreeChildrenForProject({ parentId: category.id }, cancelTokenSourceRef.current.token)
				.then((data) => {
					setCategories((prev) =>
						updateTreeBranch({
							tree: prev,
							idToReplace: category.id,
							newBranch: {
								...category,
								subThematic: data,
								expanded:
									!category.filtered && category.expanded === undefined
										? isExpanded
										: !category.expanded,
							},
							iterativeBranchName: "subThematic",
						})
					);
				})
				.catch((err) => console.error(err));
		} else {
			setCategories((prev) =>
				updateTreeBranch({
					tree: prev,
					idToReplace: category.id,
					newBranch: {
						...category,
						expanded:
							!category.filtered && category.expanded === undefined ? isExpanded : !category.expanded,
					},
					iterativeBranchName: "subThematic",
				})
			);
		}
	};
	const handleClearSearchValue = () => {
		setSearchValue("");
		setDebouncedSearchValue("");
	};
	const handleChangeSearchValue = (event) => {
		const newValue = event.target.value;
		setSearchValue(newValue);
		debouncedSearch(() => {
			setDebouncedSearchValue(newValue);
		});
	};
	const handleOpenDropDown = () => {
		setOpenDropDown(true);
	};
	const handleCloseDropDown = () => {
		setOpenDropDown(false);
	};
	const handleOpenAiCatCreation = () => {
		handleCloseDropDown();
		setOpenAiCatCreation((prev) => !prev);
	};
	const handleCloseAiCatCreation = () => {
		setOpenAiCatCreation(false);
	};
	const handleOpenImportSidePanel = () => {
		handleCloseDropDown();
		if (importMenuButtonRef.current) {
			importMenuButtonRef.current.onClose();
		}
		setOpenImportSidePanel(true);
	};
	const handleCloseImportSidePanel = () => {
		setOpenImportSidePanel(false);
	};
	const handleSubmitImportSidePanel = () => {
		handleCloseImportSidePanel();
		setNeedUpdate(true);
	};
	const handleOpenTransferSidePanel = () => {
		setOpenTransferSidePanel(true);
	};
	const handleCloseTransferSidePanel = () => {
		setOpenTransferSidePanel(false);
	};
	const handleSubmitTransferSidePanel = () => {
		handleCloseTransferSidePanel();
		setNeedUpdate(true);
	};

	const handleClickAddAction = (thematic) => {
		setTempAutomation({ thematicId: thematic.id });
		setTempCategory(thematic);
		setOpenAutomationDialog(true);
	};
	const handleCloseAutomationDialog = () => {
		setOpenAutomationDialog(false);
	};
	const handleSubmitAutomationDialog = () => {
		handleCloseAutomationDialog();
		const newCategories = updateTreeBranch({
			tree: categories,
			idToReplace: tempAutomation.thematicId,
			newBranch: {
				...tempCategory,
				actionCount: 1,
			},
			iterativeBranchName: "subThematic",
		});
		setCategories(newCategories);
	};
	const handleOpenUsersList = (category) => {
		setSelectedCategory(category);
	};
	const handleCloseSubscriptionsDialog = () => {
		setSelectedCategory(null);
	};
	const handleLaunch = (row) => {
		const { actionId } = row;
		if (actionId) {
			trigger({ projectId, actionId })
				.then(() => {
					createNotification({
						type: "success",
						message: translate("common:condensed-automation.automation.launch.success"),
					});
					setNeedUpdate(true);
				})
				.catch(console.error);
		}
	};
	const handleOpenAiCategorization = () => {
		setOpenAiCategorization(true);
	};
	const handleCloseAiCategorization = () => {
		setOpenAiCategorization(false);
	};
	const handleSelectAiCategorizationCategory = (category) => {
		setSelectedAitCategorization(category);
		handleOpenAiCategorization();
	};
	const handleSaveCategorizationAgentConfig = (
		{ thematicId },
		{ integrationPolicy, documents, additionPrompt, selectDocumentAutomatically }
	) => {
		saveCategorizationAgentConfig(
			{ thematicId, projectId },
			{
				integrationPolicy,
				documents,
				additionPrompt,
				selectDocumentAutomatically,
			}
		)
			.catch(console.error)
			.finally(handleCloseAiCategorization);
	};
	const handleSaveTreeCreationAgentConfig = (
		// eslint-disable-next-line no-unused-vars
		{ _ },
		{ integrationPolicy, documents, additionPrompt, selectDocumentAutomatically }
	) => {
		saveTreeCreationAgentConfig(
			{ projectId },
			{
				integrationPolicy,
				documents,
				additionPrompt,
				selectDocumentAutomatically,
			}
		)
			.catch(console.error)
			.finally(handleCloseAiCatCreation);
	};
	const handleLaunchAutomaticCategorization = (
		{ thematic },
		{ integrationPolicy, documents, additionPrompt, selectDocumentAutomatically }
	) => {
		saveCategorizationAgentConfig(
			{ thematicId: thematic?.id, projectId },
			{
				integrationPolicy,
				documents,
				additionPrompt,
				selectDocumentAutomatically,
			}
		).then(() => {
			launchCategorizationAgent({ thematicId: thematic?.id, projectId })
				.then(() => {
					const newCategories = updateTreeBranch({
						tree: categories,
						idToReplace: thematic.id,
						newBranch: {
							...thematic,
							agentStatus: "STARTED",
						},
						iterativeBranchName: "subThematic",
					});
					setCategories(newCategories);
				})
				.catch(console.error)
				.finally(() => {
					handleCloseAiCategorization();
					trackEvent("Categories_Content_AI_Execution");
				});
		});
	};
	const handleLaunchGlobalAutomaticCategorization = () => {
		launchGlobalCategorizationAgent({ projectId })
			.then(() => {
				getActiveCategorizations({ projectId })
					.then((data) => {
						setActiveCategorizations(data || []);
						handleCloseWarningPopUp();
					})
					.catch(console.error);
			})
			.catch(console.error)
			.finally(handleCloseAiCategorization);
	};
	const handleLaunchCategoryTreeCreation = (
		// eslint-disable-next-line no-unused-vars
		{ _ },
		{ integrationPolicy, documents, additionPrompt, selectDocumentAutomatically }
	) => {
		saveTreeCreationAgentConfig(
			{ projectId },
			{
				integrationPolicy,
				documents,
				additionPrompt,
				selectDocumentAutomatically,
			}
		).then(() => {
			launchCategoryTreeCreation({ projectId });
			handleCloseAiCatCreation();
			setIsGenerating(true);
			trackEvent("Categories_Tree_AI_Execution");
		});
	};
	const handleRollbackCategorizationAgentResults = ({ agentExecutionId, elementId }) => {
		rollbackCategorizationAgentResults({ projectId, agentExecutionId })
			.then(() => {
				const thematicFound = searchInTree({
					tree: categoriesRef.current,
					idToFind: elementId,
					iterativeBranchName: "subThematic",
				});
				getThematicInformation({ thematicId: elementId })
					.then((data) => {
						const newCategories = updateTreeBranch({
							tree: categoriesRef.current,
							idToReplace: elementId,
							newBranch: {
								...thematicFound,
								agentStatus: "FINISHED",
								agentLastModified: convertAnyDateToDisplayDate(new Date()),
								documentsCount: data.documentsCount,
								documentIds: data.documentIds,
								contentsCount: data.contentsCount,
							},
							iterativeBranchName: "subThematic",
						});
						handleCloseAiCategorization();
						setCategories(newCategories);
					})
					.catch(console.error);
			})
			.catch(console.error);
	};

	const NewCategoryButtonDropDown = (
		<>
			<ButtonGroup
				className={styles.buttonGroup}
				color="primary"
				data-testid="new.category.group.btn"
				variant="outlined"
			>
				<CustomButton
					className={styles.buttonGroup__open}
					color="primary"
					data-testid="new.category.btn"
					disabled={isGenerating}
					size="md"
					startIcon={icon.faPlus}
					variant="contained"
					onClick={handleClickAdd}
				>
					{translate("common:categories.button.new-thematic")}
				</CustomButton>
				<CustomButton
					ref={anchorRef}
					className={styles.expandBtn}
					color="primary"
					data-active={openDropDown}
					disabled={isGenerating}
					size="md"
					startIcon={(openDropDown && icon.faChevronUp) || icon.faChevronDown}
					variant="contained"
					onClick={() => handleOpenDropDown()}
				/>
			</ButtonGroup>
			<DropDown
				ref={anchorRef}
				className={styles.newCatDropDown}
				open={openDropDown}
				onClose={handleCloseDropDown}
			>
				<MenuList className={styles.newCatList}>
					<MenuItem className={styles.newCatItem} onClick={handleOpenImportSidePanel}>
						<IconComponent
							className={styles.newCatItem__icon}
							color="var(--grey-600)"
							icon={icon.faDownload}
							size="xs"
						/>
						<span className={styles.newCatItem__text}>
							{translate("admin-categories.button.import-categories")}
						</span>
					</MenuItem>
					{isSegFeatureEnabled(SegFlags.AI_CATEGORY_TREE_CREATOR) && isAllowedToEditStrict && (
						<CustomTooltip
							title={
								(!hasAvailableAIQuota &&
									translate("common:company.quotas.consumption-reached.tooltip")) ||
								""
							}
						>
							<span>
								<MenuItem
									className={styles.newCatItem}
									disabled={isNonEmptyArray(activeCategorizations) || !hasAvailableAIQuota}
									onClick={handleOpenAiCatCreation}
								>
									<IconComponent
										className={styles.newCatItem__icon}
										color="var(--grey-600)"
										disabled={isNonEmptyArray(activeCategorizations)}
										icon={icon.faWandMagicSparkles}
									/>
									<span className={styles.newCatItem__text}>
										{translate("admin-categories.button.generate-categories")}
									</span>
								</MenuItem>
							</span>
						</CustomTooltip>
					)}
				</MenuList>
			</DropDown>
		</>
	);
	const TransferButton = (
		<CustomButton
			className={styles.transferBtn}
			color="secondary"
			disabled={!Array.isArray(categories) || categories.length === 0}
			size="md"
			startIcon={icon.faSave}
			variant="outlined"
			onClick={handleOpenTransferSidePanel}
		>
			{translate("common:categories.button.transfer")}
		</CustomButton>
	);
	const NewCategoryButton = (
		<CustomButton
			className={styles.buttonGroup__open}
			color="primary"
			data-testid="new.category.btn"
			disabled={isGenerating}
			size="md"
			startIcon={icon.faPlus}
			variant="contained"
			onClick={handleClickAdd}
		>
			{translate("common:categories.button.new-thematic")}
		</CustomButton>
	);
	const ImportButton = (
		<>
			<CustomButton
				color="secondary"
				disabled={isGenerating}
				size="md"
				startIcon={icon.faFileImport}
				variant="outlined"
				onClick={handleOpenImportSidePanel}
			>
				{translate("admin-categories.button.import")}
			</CustomButton>
		</>
	);
	const CategorizeContentButton = (
		<CustomButton
			color="primary"
			data-testid="categorize.content.btn"
			disabled={isGenerating || isNonEmptyArray(activeCategorizations)}
			size="md"
			startIcon={icon.faWandMagicSparkles}
			variant="outlined"
			onClick={handleOpenWarningPopUp}
		>
			{translate("common:component.ai-config.categorize-content.button")}
		</CustomButton>
	);
	const AdditionalEmptyStateComponent = (
		<div className={styles.emptyStateAdditionalContent}>
			<div>
				<IconComponent color="var(--blue-500)" icon={icon.faWandMagicSparkles} />
			</div>
			<div>
				<div className={styles.isGeneratingContainer__title}>
					{translate("admin-categories.empty-state.additional-centent.title")}
				</div>
				<div className={styles.isGeneratingContainer__helper}>
					{translate("admin-categories.empty-state.additional-centent.helper")}
				</div>
				<CustomTooltip
					title={
						(!hasAvailableAIQuota && translate("common:company.quotas.consumption-reached.tooltip")) || ""
					}
				>
					<span>
						<CustomButton
							className={styles.emptyStateAdditionalContent__btn}
							disabled={isGenerating || isNonEmptyArray(activeCategorizations) || !hasAvailableAIQuota}
							variant="contained"
							onClick={handleOpenAiCatCreation}
						>
							{translate("admin-categories.empty-state.additional-centent.launch")}
						</CustomButton>
					</span>
				</CustomTooltip>
			</div>
		</div>
	);
	return (
		<>
			{isGenerating && (
				<AlertBanner
					iconSpin
					className={styles.isGeneratingContainer}
					description={translate("admin-categories.is-generating-banner.helper")}
					iconElement={icon.faSpinner}
					title={translate("admin-categories.is-generating-banner.title")}
				/>
			)}
			<div className={styles.mainContainer}>
				<div className={styles.searchContainer}>
					{((!isTreeLoading && isNonEmptyArray(categories)) || searchValue) && (
						<SearchInput
							className={styles.searchContainer__textfield}
							label={translate("common:categories.textfield.label")}
							value={searchValue}
							onChange={handleChangeSearchValue}
							onClearSearch={handleClearSearchValue}
						/>
					)}
					<div className={styles.buttonsContainer}>
						{!isTreeLoading && isAllowedToEdit && TransferButton}
						{!isTreeLoading &&
							isAllowedToEdit &&
							// TODO IN_FUTURE remove SegFlags.DISABLED to re-enable global categorization
							isSegFeatureEnabled([SegFlags.AI_CONTENT_CATEGORIZER, SegFlags.DISABLED]) &&
							isNonEmptyArray(categories) &&
							CategorizeContentButton}
						{!isTreeLoading && isAllowedToEdit && NewCategoryButtonDropDown}
					</div>
				</div>
				<div
					className={styles.mainContainer__list}
					data-is-generating={isGenerating}
					data-testid="category.list"
				>
					{(isTreeLoading && (
						<div className={styles.mainContainer__loader}>
							<CircularLoader />
						</div>
					)) ||
						(isNonEmptyArray(categories) && (
							<>
								<HeaderCategoryRow
									disableCheckboxes
									isAdminCategories
									categoriesCount={(categories || []).length}
									isAiVersion={isSegFeatureEnabled(SegFlags.AI_CONTENT_CATEGORIZER)}
									isChecked={false}
									isEditable={isAllowedToEdit}
									projectId={projectId}
								/>
								{categories.map((category) => (
									<ExpandableCategoryRow
										key={category.id}
										isAdminCategories
										activeCategorizations={activeCategorizations}
										category={category}
										expanded={
											(category.filtered === false && category.expanded === undefined) ||
											category.expanded
										}
										isAiVersion={isSegFeatureEnabled(SegFlags.AI_CONTENT_CATEGORIZER)}
										isEditable={isAllowedToEdit}
										projectId={projectId}
										onClickAdd={handleClickAdd}
										onClickAddAction={handleClickAddAction}
										onClickDelete={handleClickDelete}
										onClickEdit={handleClickEdit}
										onOpenUsersList={handleOpenUsersList}
										onSelectAiCategorization={handleSelectAiCategorizationCategory}
										onToggleExpand={handleToggleExpand}
									/>
								))}
							</>
						)) || (
							<EmptyState
								additionalComponent={
									isSegFeatureEnabled(SegFlags.AI_CATEGORY_TREE_CREATOR) &&
									isAllowedToEditStrict &&
									AdditionalEmptyStateComponent
								}
								btn1={ImportButton}
								btn2={NewCategoryButton}
								title={
									searchValue
										? translate("admin-categories.empty-state.filter")
										: translate("admin-categories.empty-state")
								}
							/>
						)}
				</div>
			</div>
			<CategoryDialog
				hasAiCategorization
				isAdminCategories
				activeCategorizations={activeCategorizations}
				availableParents={!searchValue && categories}
				category={tempCategory}
				classNameDialog={`${openAiCategorization && styles["dialog--side-panel-open"]}`}
				isNew={isNew}
				open={openCategoryDialog}
				parentCategory={tempParent}
				onClose={handleCloseCategoryDialog}
				onGetAutomations={{ getAutomations, cancelGetAutomations }}
				onGetFilters={getFilters}
				onGetHistory={getCategorizationLaunchHistory}
				onGetInformation={getParametersForInformation}
				onLaunch={handleLaunch}
				onRollbackCategorizationAgentResults={handleRollbackCategorizationAgentResults}
				onSave={handleSaveCategorizationAgentConfig}
				onSubmit={handleSubmitCategoryDialog}
				onUpdateFilters={updateFilters}
			/>
			<CategoryDeleteDialog
				categoryToDelete={tempCategory}
				open={openDeleteDialog}
				parentCategory={tempParent}
				onClose={handleCloseDeleteDialog}
				onSubmit={handleSubmitDeleteDialog}
			/>
			<ImportTransferCategorySidePanel
				importing={openImportSidePanel}
				open={openImportSidePanel || openTransferSidePanel}
				title={translate(
					(openImportSidePanel && "common:categories.import-panel.title") ||
						"common:categories.transfer-panel.title"
				)}
				onClose={(openImportSidePanel && handleCloseImportSidePanel) || handleCloseTransferSidePanel}
				onSubmit={(openImportSidePanel && handleSubmitImportSidePanel) || handleSubmitTransferSidePanel}
			/>
			<AutomationDialog
				isNew
				action={tempAutomation}
				availableParents={!searchValue && categories}
				open={openAutomationDialog}
				onClose={handleCloseAutomationDialog}
				onSubmit={handleSubmitAutomationDialog}
			/>
			{isSegFeatureEnabled(SegFlags.FAVORITE_CATEGORIES) && (
				<CategorySubscriptionsDialog
					category={selectedCategory}
					open={!!selectedCategory}
					onClose={handleCloseSubscriptionsDialog}
				/>
			)}
			<CustomDialog
				iconColor="var(--blue-500)"
				iconModel={icon.faInfoCircle}
				open={openWarningPopup}
				submitLabel={translate("common:btn.confirm")}
				title={translate("admin-categories.warning-dialog-title")}
				onClose={handleCloseWarningPopUp}
				onSubmit={handleLaunchGlobalAutomaticCategorization}
			>
				<div>
					<div className={styles.warningSubtitle}>
						{translate("admin-categories.warning-dialog-sub-title")}
					</div>
					<div>{translate("admin-categories.warning-dialog-sub-title.continue")}</div>
				</div>
			</CustomDialog>
			<AiAgentConfigDialog
				actionOptions={categorizationActionOptions}
				actionOptionsTitle={translate("common:component.ai-config.dialog.categorization.actions-title")}
				documentOptions={categorizationDocOptions}
				documentOptionsTitle={translate("common:component.ai-config.dialog.categorization.document-title")}
				element={selectedAitCategorization}
				header={
					<>
						<div className={styles.header__name}>{selectedAitCategorization?.name}</div>
						{selectedAitCategorization?.description && (
							<div className={styles.header__description}>{selectedAitCategorization?.description}</div>
						)}
					</>
				}
				helperText={translate("common:component.ai-config.dialog.title-helper")}
				historyHeaderTitleKey="common:ai-config-dialog.history-row.categorization"
				launchTitle={translate("common:component.ai-config.dialog.categorization.launch.btn")}
				open={openAiCategorization}
				promptHoverText={translate("common:ai-config-dialog.categorization.hover")}
				promptModelHelperText={translate("common:component.ai-config.prompt-models.helper-text")}
				promptModels={aiCategorizationPromptModels}
				promptPlaceholder={translate("common:ai-config-dialog.categorization.placeholder")}
				templateSelectEventMessage="Categories_Content_AI_applies_template"
				title={translate("common:component.ai-config.dialog.title")}
				onClose={handleCloseAiCategorization}
				onGetHistory={getCategorizationLaunchHistory}
				onGetInformation={getParametersForInformation}
				onLaunch={handleLaunchAutomaticCategorization}
				onRollbackAgentResults={handleRollbackCategorizationAgentResults}
				onSave={handleSaveCategorizationAgentConfig}
			/>
			<AiAgentConfigDialog
				documentOptions={aiCreationDocOptions}
				documentOptionsTitle={translate(
					"admin-categories.ai-config-dialog.category-tree.document-options-title"
				)}
				helperText={translate("common:component.ai-category-tree.dialog.title-helper")}
				historyHeaderTitleKey="admin-categories.ai-config-dialog.history-row.category-tree"
				launchTitle={translate("admin-categories.ai-config-dialog.category-tree.launch.btn")}
				open={openAiCatCreation}
				promptHoverText={translate("admin-categories.ai-config-dialog.category-tree.hover")}
				promptModelHelperText={translate("admin-categories.ai-config.prompt-models.helper-text")}
				promptModels={aiCreationPromptModels}
				promptPlaceholder={translate("admin-categories.ai-config-dialog.category-tree.placeholder")}
				promptTitle={translate("admin-categories.ai-config-dialog.category-tree.title")}
				templateSelectEventMessage="Categories_Tree_AI_applies_template"
				title={translate("common:component.ai-category-tree.dialog.title")}
				onClose={handleCloseAiCatCreation}
				onGetHistory={getTreeCreationAgentHistory}
				onGetInformation={getTreeCreationAgentConfig}
				onLaunch={handleLaunchCategoryTreeCreation}
				onSave={handleSaveTreeCreationAgentConfig}
			/>
		</>
	);
};

export default exportView({
	path: "/projects/:projectId/project-categories",
	localesPath: "/admin-categories/locales",
	component: withWebsocket(AdminCategories),
	flag: Flags.THEMATIC,
	segFlag: SegFlags.CATEGORY,
});
